import { useState } from 'react';
import fieldWorkService from '../../services/fieldWork';
import {
  WORKER_TYPE_LABELS,
  type LabourProvider,
  type Worker,
  type WorkerInput,
  type WorkerType,
} from '../../types/fieldWork';
import { errorMessage } from '../../utils/apiResponse';
import FormModal, { Field, inputClass } from './FormModal';

interface WorkerFormModalProps {
  worker: Worker | null; // null = new
  providers: LabourProvider[];
  onClose: () => void;
  onSaved: (w: Worker) => void;
}

export default function WorkerFormModal({ worker, providers, onClose, onSaved }: WorkerFormModalProps) {
  const [form, setForm] = useState<WorkerInput>({
    first_name: worker?.first_name ?? '',
    last_name: worker?.last_name ?? '',
    preferred_name: worker?.preferred_name ?? '',
    worker_type: worker?.worker_type ?? 'casual',
    labour_provider_id: worker?.labour_provider_id ?? null,
    picker_code: worker?.picker_code ?? '',
    phone: worker?.phone ?? '',
    start_date: worker?.start_date ?? '',
    end_date: worker?.end_date ?? '',
    notes: worker?.notes ?? '',
    active: worker?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof WorkerInput>(k: K, v: WorkerInput[K]) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload: WorkerInput = {
      ...form,
      first_name: form.first_name.trim(),
      preferred_name: form.preferred_name || null,
      phone: form.phone || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      notes: form.notes || null,
      picker_code: form.picker_code?.trim() || null,
    };
    if (!worker) delete payload.active;
    try {
      const saved = worker
        ? await fieldWorkService.updateWorker(worker.id, payload)
        : await fieldWorkService.createWorker(payload);
      onSaved(saved);
    } catch (e) {
      setError(errorMessage(e, 'Could not save the worker'));
    } finally {
      setSaving(false);
    }
  };

  const activeProviders = providers.filter(p => p.active || p.id === form.labour_provider_id);

  return (
    <FormModal
      title={worker ? 'Edit worker' : 'Add worker'}
      onClose={onClose}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={!form.first_name.trim()}
      error={error}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="First name" htmlFor="w-first">
          <input id="w-first" className={inputClass} value={form.first_name} onChange={e => set('first_name', e.target.value)} autoFocus />
        </Field>
        <Field label="Last name" htmlFor="w-last">
          <input id="w-last" className={inputClass} value={form.last_name ?? ''} onChange={e => set('last_name', e.target.value)} />
        </Field>
      </div>
      <Field label="Goes by (optional)" htmlFor="w-pref">
        <input id="w-pref" className={inputClass} value={form.preferred_name ?? ''} onChange={e => set('preferred_name', e.target.value)} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Type" htmlFor="w-type">
          <select id="w-type" className={inputClass} value={form.worker_type}
            onChange={e => set('worker_type', e.target.value as WorkerType)}>
            {(Object.keys(WORKER_TYPE_LABELS) as WorkerType[]).map(t => (
              <option key={t} value={t}>{WORKER_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </Field>
        <Field label="Labour provider" htmlFor="w-provider">
          <select id="w-provider" className={inputClass} value={form.labour_provider_id ?? ''}
            onChange={e => set('labour_provider_id', e.target.value ? Number(e.target.value) : null)}>
            <option value="">None (our own)</option>
            {activeProviders.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </div>
      <Field
        label="Picker code"
        htmlFor="w-code"
        hint={worker ? 'Printed on their card. Change only if you are reissuing the card.' : 'Leave empty to generate one (e.g. W0012-4), or enter the number on a card they already have.'}
      >
        <input id="w-code" className={`${inputClass} font-mono uppercase`} value={form.picker_code ?? ''}
          placeholder={worker ? '' : 'Generated'} onChange={e => set('picker_code', e.target.value)} />
      </Field>
      <Field label="Phone (optional)" htmlFor="w-phone">
        <input id="w-phone" type="tel" className={inputClass} value={form.phone ?? ''} onChange={e => set('phone', e.target.value)} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Start date" htmlFor="w-start">
          <input id="w-start" type="date" className={inputClass} value={form.start_date ?? ''} onChange={e => set('start_date', e.target.value)} />
        </Field>
        <Field label="End date" htmlFor="w-end">
          <input id="w-end" type="date" className={inputClass} value={form.end_date ?? ''} onChange={e => set('end_date', e.target.value)} />
        </Field>
      </div>
      <Field label="Notes (optional)" htmlFor="w-notes">
        <textarea id="w-notes" rows={2} className={inputClass} value={form.notes ?? ''} onChange={e => set('notes', e.target.value)} />
      </Field>
      {worker && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.active ?? true} onChange={e => set('active', e.target.checked)}
            className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
          <span className="text-neutral-700">Active (untick when they finish up — their history is kept)</span>
        </label>
      )}
    </FormModal>
  );
}
